import { useQuery } from "@tanstack/react-query"
import * as Tabs from "@radix-ui/react-tabs"
import { useTranslation } from "react-i18next"
import { useAdminAuth } from "@/hooks/useAdminAuth"
import { useAdminAreal } from "@/hooks/useAdminAreal"
import { ReservationTable } from "@/components/ReservationTable"
import { api } from "@/lib/api"
import { formatCurrency, LOCALE_MAP } from "@/lib/utils"
import { cn } from "@/lib/utils"
import type { AdminReservationRow } from "@fohet/caravan-booker-shared/types"

interface ReservationListResponse {
    items: AdminReservationRow[]
    total: number
    page: number
    perPage: number
}

export const AdminDashboardPage = () => {
    const { t, i18n } = useTranslation()
    const { user } = useAdminAuth()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const { data: areal } = useAdminAreal()

    const queryKey = ["admin", "reservations", areal?.id]
    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () => api.get<ReservationListResponse>(
            `/reservations${areal?.id ? `?arealId=${areal.id}` : ""}`
        ),
        enabled: areal !== undefined,
    })

    const reservations = data?.items ?? []

    // Derived stats
    const totalRevenue = reservations
        .filter((r) => r.paymentStatus === "paid")
        .reduce((sum, r) => sum + r.totalAmount, 0)

    const pendingCount = reservations.filter((r) => r.paymentStatus === "pending" && r.status !== "cancelled").length
    const activeCount = reservations.filter((r) => r.status === "active" || r.status === "confirmed").length

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900">{t("admin.dashboard.title")}</h1>
                <p className="text-slate-500 font-bold mt-1">{t("admin.dashboard.welcome", { email: user?.email })}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-6">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">{t("admin.dashboard.totalReservations")}</p>
                    <p className="text-3xl font-black text-slate-900">{reservations.length}</p>
                </div>
                <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-6">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">{t("admin.dashboard.pendingPayment")}</p>
                    <p className="text-3xl font-black text-orange-500">{pendingCount}</p>
                </div>
                <div className="bg-charcoal rounded-[28px] shadow-2xl p-6">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">{t("admin.dashboard.revenue")}</p>
                    <p className="text-3xl font-black text-brand">{formatCurrency(totalRevenue, areal?.currency ?? "CZK", locale)}</p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs.Root defaultValue="reservations">
                <Tabs.List className="flex gap-1 bg-slate-100 rounded-2xl p-1 w-fit mb-5">
                    {[
                        { value: "reservations", label: t("admin.dashboard.tabAll") },
                        { value: "pending", label: t("admin.dashboard.tabPending", { count: pendingCount }) },
                        { value: "active", label: t("admin.dashboard.tabActive", { count: activeCount }) },
                    ].map(({ value, label }) => (
                        <Tabs.Trigger
                            key={value}
                            value={value}
                            className={cn(
                                "px-4 py-2 rounded-2xl text-sm font-black transition-all duration-200",
                                "text-slate-400 hover:bg-slate-50",
                                "data-[state=active]:bg-charcoal data-[state=active]:text-white data-[state=active]:shadow-lg",
                            )}
                        >
                            {label}
                        </Tabs.Trigger>
                    ))}
                </Tabs.List>

                <Tabs.Content value="reservations">
                    {isLoading ? (
                        <div className="text-slate-400 font-bold py-8 text-center">{t("admin.dashboard.loading")}</div>
                    ) : (
                        <ReservationTable reservations={reservations} queryKey={queryKey} currency={areal?.currency ?? "CZK"} />
                    )}
                </Tabs.Content>

                <Tabs.Content value="pending">
                    <ReservationTable
                        reservations={reservations.filter((r) => r.paymentStatus === "pending" && r.status !== "cancelled")}
                        queryKey={queryKey}
                        currency={areal?.currency ?? "CZK"}
                    />
                </Tabs.Content>

                <Tabs.Content value="active">
                    <ReservationTable
                        reservations={reservations.filter((r) => r.status === "active" || r.status === "confirmed")}
                        queryKey={queryKey}
                        currency={areal?.currency ?? "CZK"}
                    />
                </Tabs.Content>
            </Tabs.Root>
        </div>
    )
}
